import { decryptWithDEK, encryptWithDEK } from "@/features/encryption/utils/encryption";

// Types for encrypted tag data
export type EncryptedTagName =
  | {
      encrypted_data: string;
      iv: string;
    }
  | {
      encrypted_data: null;
      iv: null;
    };

/**
 * Encrypt tag name before sending to server
 * Only encrypts if encryption is enabled
 */
export async function encryptTagName(name: string | null | undefined, dek: CryptoKey): Promise<EncryptedTagName> {
  try {
    const nameToEncrypt = name || "";
    const { iv, ciphertext } = await encryptWithDEK(dek, nameToEncrypt);
    return {
      encrypted_data: ciphertext,
      iv,
    };
  } catch (error) {
    console.error("Failed to encrypt tag name:", error);
    return { encrypted_data: null, iv: null };
  }
}

/**
 * Decrypt tag name received from server
 * Only decrypts if encryption is enabled and data is available
 */
export async function decryptTagName(encryptedData: string, iv: string, dek: CryptoKey): Promise<string> {
  try {
    return await decryptWithDEK(dek, encryptedData, iv);
  } catch (error) {
    console.error("Failed to decrypt tag name:", error);
    return "";
  }
}

/**
 * Process tag name by decrypting if encryption is enabled
 * Returns decrypted name or original name if no encryption
 */
export async function processTagName(
  tag: {
    name: string | null;
    encrypted_data: string | null;
    iv: string | null;
  },
  dek: CryptoKey | null | undefined
): Promise<string> {
  if (dek && tag.encrypted_data && tag.iv) {
    return decryptTagName(tag.encrypted_data, tag.iv, dek);
  }
  return tag.name || "";
}

/**
 * Prepare encrypted name for database operations
 * Returns encrypted data if available, otherwise null values
 */
export async function prepareEncryptedTagName(
  name: string | null | undefined,
  dek: CryptoKey | null | undefined
): Promise<{
  encrypted_data: string | null;
  iv: string | null;
  clearName: boolean;
}> {
  if (!dek) {
    return { encrypted_data: null, iv: null, clearName: false };
  }

  const result = await encryptTagName(name, dek);
  return {
    ...result,
    clearName: result.encrypted_data !== null && result.iv !== null,
  };
}
